import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import ReportingCurrencyPrompt from '@/components/ReportingCurrencyPrompt';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDownRight, ArrowUpRight, ArrowRight, Compass, Lightbulb, PieChart as PieChartIcon, ShieldCheck, Sparkles, TrendingUp, Wallet, HeartPulse, Scale, Plus, Upload, Target, Calendar, RotateCcw, PiggyBank, CreditCard, Repeat, MessageCircleQuestion } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, Legend } from 'recharts';
import { CATEGORY_COLORS, colorForCategory } from '@/lib/categorize';
// BUG-002 — one shared income rule (category set + loan-drawdown narrations).
import { isRealIncome } from '@/lib/interimPolicy';
import { formatMoney, formatPercent } from '@/lib/format';
import { isInvestmentTransfer } from '@/lib/investmentTransferRules';
import { Link, useNavigate } from 'react-router-dom';
import { slugify, spendingByAccount as computeSpendingByAccount } from '@/lib/detailHelpers';
import axios from 'axios';

import usePageTitle from '@/hooks/usePageTitle';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import { readCache, writeCache, withTimeout } from '@/lib/pageCache';
import { resolveRange, txnInRange } from '@/lib/dateRange';
import { fetchDashboardBreakdown, fetchNetWorthBreakdown } from '@/lib/currency';
import FxBreakdownCard from '@/components/FxBreakdownCard';
import LifeProfileBanner from '@/components/LifeProfileBanner';
import CategoryRemapNoticeBanner from '@/components/CategoryRemapNoticeBanner';
import { QuestionCard } from '@/components/QuestionCard';
import DnaCompactCard from '@/components/DnaCompactCard';
import UploadReminderTile from '@/components/UploadReminderTile';
import useUploadReminder from '@/hooks/useUploadReminder';
import DebtFocusTable from '@/components/liabilities/DebtFocusTable';
import { buildNetWorthJourney } from '@/lib/netWorthJourney';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Dashboard scope chips (no Custom, keep dashboard quiet).
const SCOPE_PRESETS = [
  { value: 'last_7',     label: 'Last 7 days' },
  { value: 'last_30',    label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'all',        label: 'All' },
];

export default function Dashboard() {
  usePageTitle('Dashboard');
  const { user, profile, session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  // Phase 38, base_currency is the headline. Fall back to legacy `currency`
  // if the migration hasn't been applied yet so we don't crash older envs.
  const base = (profile?.base_currency || profile?.currency || 'INR').toUpperCase();
  const currency = base; // alias; legacy code paths below still say `currency`
  // Multi-currency breakdown, populated when the user has accounts in more
  // than one currency. When present, KPIs use the base-converted totals
  // (correct math) instead of summing raw transaction amounts (wrong math
  // for multi-currency users).
  const [fxBreakdown, setFxBreakdown] = useState(null);
  // Phase 55 follow-up — holistic net worth. The Dashboard previously
  // computed net worth from `holdings` alone, which meant users who'd only
  // added bank accounts (or only deposits) saw ₹0 here even though their
  // money was clearly in the app. The backend now aggregates accounts +
  // deposits + holdings; we use its total when available, falling back to
  // the local holdings-only calc until the API resolves.
  const [networthBreakdown, setNetworthBreakdown] = useState(null);
  // Phase 55 follow-up #6 — light-weight account count for the next-best-
  // step nudge. We only need the row count, not the full account rows,
  // so this is a cheap separate query rather than a duplicate fetch on
  // the Accounts page's full schema.
  const [accountCount, setAccountCount] = useState(null);
  // Phase 56 — Count accounts where the user hasn't yet entered a manual
  // balance. We surface a calm nudge on the Dashboard inviting them to
  // do so, since accurate Net Worth + Runway depend on it.
  const [accountsMissingBalance, setAccountsMissingBalance] = useState(0);
  // Phase 57 — full accounts list (id + name + type + currency) for the
  // Spending-by-account widget. Kept light so it adds ~1 cheap query.
  const [allAccountsFull, setAllAccountsFull] = useState([]);

  // Phase 30, Safari tab-suspension resilience.
  // We hydrate every state slice synchronously from sessionStorage (via
  // `readCache`) on first render. After Safari purges the tab's JS heap and
  // re-mounts this component, the user sees their last-known data instantly
  // instead of an empty/loading state. The background refetch below then
  // silently brings it up to date, and if that refetch fails or times out,
  // the hydrated data stays put. No flicker, no "No data yet" empty state,
  // no forced refresh.
  const uid = user?.id || null;
  const cached = uid ? (readCache(uid, 'dashboard:v1') || {}) : {};

  const [txns, setTxns] = useState(cached.txns || []);
  const [holdings, setHoldings] = useState(cached.holdings || []);
  const [onb, setOnb] = useState(cached.onb || null);
  const [goals, setGoals] = useState(cached.goals || []);
  const [recurring, setRecurring] = useState(cached.recurring || []);
  // v3.7.5 Phase 84.29 — Full liability rows (name, kind, interest_rate,
  // outstanding, emi_amount, currency) for the Debt Focus preview card.
  // Kept separate from `holdings` because that array is normalised to
  // the legacy {kind:'liability', value} shape used by the rest of the
  // dashboard reducers.
  const [liabilities, setLiabilities] = useState(cached.liabilities || []);
  // Only show the loading skeleton if we have NO cached data to render.
  // After a Safari heap purge + remount, `cached` is populated → loading=false
  // immediately, so the previously-rendered page reappears in one frame.
  const [loading, setLoading] = useState(!cached.txns);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  // Phase B — Question Feed dashboard tile (top 3 open).
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  // Phase 74B — Financial DNA card
  const [dna, setDna] = useState(null);
  const [dnaLoading, setDnaLoading] = useState(false);
  // Full snapshot payload — used by the upload reminder tile to read
  // last_txn_date and flags.
  const [snapshot, setSnapshot] = useState(null);
  // Date-scope chip (applies to KPI cards + category donut + top categories).
  // The 6-month trend charts are deliberately scope-independent.
  const [scopePreset, setScopePreset] = useState('this_month');
  // Tracks whether we've successfully loaded data at least once. On refocus
  // (Supabase TOKEN_REFRESHED → new user object → effect re-fires), we skip
  // the skeleton state and refetch silently so the UI never blanks out.
  const hasLoadedOnceRef = useRef(!!cached.txns);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!user?.id) return;
    if (!silent && !hasLoadedOnceRef.current) setLoading(true);
    try {
      const since = new Date(); since.setMonth(since.getMonth() - 6);
      const sinceISO = (() => {
        const d = since;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })();
      // Wrap each query in a 12s timeout so a hung Supabase SDK call (known
      // failure mode after a long tab background → mid-token-refresh) can't
      // freeze the page indefinitely.
      const [tx, o, w, lia, accs, g, r] = await Promise.all([
        withTimeout(supabase.from('transactions').select('*').gte('txn_date', sinceISO).order('txn_date', { ascending: false }), 12000, 'dashboard.txns').then((x) => x?.data || null),
        withTimeout(supabase.from('onboarding_profiles').select('*').eq('user_id', user.id).maybeSingle(), 12000, 'dashboard.onb').then((x) => x?.data || null),
        // Phase 60D — split legacy `holdings` into wealth_holdings (asset side)
        // + liabilities (debt side). The backend `/api/networth/breakdown`
        // is the source of truth; these client-side reads only feed the
        // pre-breakdown skeleton estimates so a cold dashboard doesn't show
        // ₹0 while the API call is in flight.
        withTimeout(supabase.from('wealth_holdings').select('id, kind, value, currency'), 12000, 'dashboard.wealth').then((x) => x?.data || null),
        withTimeout(supabase.from('liabilities').select('id, name, kind, outstanding, interest_rate, emi_amount, currency'), 12000, 'dashboard.liab').then((x) => x?.data || null),
        // v3.7.5 Phase 84.29 — also pull loan/mortgage-typed rows from the
        // `accounts` table so the Debt Focus preview surfaces users whose
        // lump-sum debt lives there (Priya persona seeds the Toyota +
        // HDFC loans this way). Kept as a separate query rather than
        // reusing useAccountsData because Dashboard.js still runs its
        // own timeout/parallel pattern and we want to fail closed if
        // this specific fetch times out.
        withTimeout(
          supabase.from('accounts')
            .select('id, name, type, balance, interest_rate, next_payment_amount, minimum_payment, currency')
            .in('type', ['loan', 'auto_loan', 'mortgage', 'education_loan', 'personal_loan', 'other_debt']),
          12000, 'dashboard.acct_loans',
        ).then((x) => x?.data || null),
        withTimeout(supabase.from('goals').select('*').eq('status', 'active').order('created_at', { ascending: false }).limit(2), 12000, 'dashboard.goals').then((x) => x?.data || null),
        withTimeout(supabase.from('recurring_expenses').select('*').eq('user_id', user.id), 12000, 'dashboard.recurring').then((x) => x?.data || null),
      ]);
      // Normalise (wealth + liabilities) into the legacy `holdings` shape so
      // the rest of Dashboard.js (which uses h.kind ∈ {asset, liability})
      // keeps working without touching every reducer.
      const h = (w !== null || lia !== null) ? [
        ...((w || []).map((x) => ({ id: x.id, kind: 'asset',     value: x.value,       currency: x.currency }))),
        ...((lia || []).map((x) => ({ id: x.id, kind: 'liability', value: x.outstanding, currency: x.currency }))),
      ] : null;
      // Only update state when the fetch ACTUALLY succeeded (non-null). A
      // null result from a failed/timed-out call must NOT clobber the cached
      // data we already painted, that's the whole point.
      const nextTxns = tx ?? txns;
      const nextHoldings = h ?? holdings;
      const nextOnb = o ?? onb;
      const nextGoals = g ?? goals;
      const nextRecurring = r ?? recurring;
      setTxns(nextTxns);
      setHoldings(nextHoldings);
      setOnb(nextOnb);
      setGoals(nextGoals);
      setRecurring(nextRecurring);
      // v3.7.5 Phase 84.29 — Merge liabilities-table rows with any
      // loan/mortgage-typed `accounts` rows so the Debt Focus preview
      // surfaces users whose lump-sum debt lives in either source. Both
      // are adapted to the shape DebtFocusTable expects (name, kind,
      // outstanding, interest_rate, emi_amount, currency).
      const liaRows = (lia ?? []).map((x) => ({
        id: x.id, name: x.name, kind: x.kind,
        outstanding: x.outstanding, interest_rate: x.interest_rate,
        emi_amount: x.emi_amount, currency: x.currency,
      }));
      const acctLoanRows = (accs ?? []).map((x) => ({
        id: x.id, name: x.name, kind: x.type,
        outstanding: Math.abs(Number(x.balance || 0)),
        interest_rate: x.interest_rate,
        emi_amount: x.next_payment_amount || x.minimum_payment,
        currency: x.currency,
      }));
      const nextLiabilities = (lia === null && accs === null)
        ? liabilities
        : [...liaRows, ...acctLoanRows];
      setLiabilities(nextLiabilities);
      // Mirror to cache so a future Safari remount can hydrate instantly.
      writeCache(user.id, 'dashboard:v1', {
        txns: nextTxns, holdings: nextHoldings, onb: nextOnb,
        goals: nextGoals, recurring: nextRecurring, liabilities: nextLiabilities,
      });
      hasLoadedOnceRef.current = true;
    } catch (e) {
      // Keep existing data on screen; just log. Common case here is
      // `e.name === 'TimeoutError'` after a refocus mid-token-refresh.
      console.warn('Dashboard load failed (keeping prior data):', e?.message || e);
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadDashboard({ silent: false });
    // Only refetch when a different user signs in. Token refresh keeps the
    // same `user.id` so we don't blank the UI on tab refocus.
  }, [user?.id, loadDashboard]);

  // Phase 38.4, fetch the multi-currency dashboard breakdown alongside
  // the regular data load. Triggers on access-token presence + period
  // change. Failures fall through silently to the legacy single-currency
  // KPI math (which is correct for single-currency users anyway).
  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    // Map dashboard scope chips → backend period buckets.
    const period = scopePreset === 'last_30' ? 'last30d'
                 : scopePreset === 'this_month' ? 'month'
                 : 'month';
    (async () => {
      const data = await fetchDashboardBreakdown(session.access_token, period);
      if (!cancelled) setFxBreakdown(data);
    })();
    // Holistic net-worth refresh — independent of the dashboard scope chip
    // (net worth is point-in-time, not period-scoped). Best-effort: a
    // failure here keeps the holdings-only fallback alive.
    (async () => {
      const nw = await fetchNetWorthBreakdown(session.access_token);
      if (!cancelled && nw) setNetworthBreakdown(nw);
    })();
    // Phase 55 follow-up #6 — accounts count for the next-best-step
    // nudge (separate cheap query; we only need the count to decide
    // whether to suggest "add another account").
    // Phase 56 — also count how many accounts are missing a manual balance,
    // since Net Worth + Runway accuracy depend on it. We exclude deposits
    // here (FDs / RDs carry their principal as the balance automatically).
    (async () => {
      try {
        const { data, error } = await supabase
          .from('accounts')
          .select('id, name, type, balance, currency');
        if (!cancelled && !error) {
          const rows = data || [];
          setAccountCount(rows.length);
          setAllAccountsFull(rows);
          const missing = rows.filter((a) => {
            const t = (a.type || '').toLowerCase();
            if (t === 'deposit') return false;
            const b = Number(a.balance || 0);
            return !b;
          }).length;
          setAccountsMissingBalance(missing);
        }
      } catch { /* nudge will fall through to a different suggestion */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, scopePreset]);

  // Silently refetch when the tab returns from a long Safari background.
  // No skeleton, no blanking, the visible data just updates if the network
  // call succeeds, and stays as-is if it doesn't.
  useFocusRefetch(() => loadDashboard({ silent: true }), { minHiddenMs: 60_000 });

  // Resolve the active scope (timezone-safe) to {from,to} YYYY-MM-DD strings.
  const scopeRange = useMemo(() => resolveRange(scopePreset, null, null), [scopePreset]);
  const scopeLabel = useMemo(
    () => (SCOPE_PRESETS.find((p) => p.value === scopePreset) || { label: 'This month' }).label.toLowerCase(),
    [scopePreset],
  );

  const metrics = useMemo(() => {
    const now = new Date();
    const { from, to } = scopeRange;
    // Period-scoped transactions for KPIs / donut / top categories.
    // For "all" both bounds are null → includes everything we fetched.
    const scopedTx = txns.filter((t) => txnInRange(t.txn_date, from, to));
    // Phase 56 — Unified sign convention: ALL outflow magnitudes (expenses,
    // investments, refunds) are computed as POSITIVE numbers via abs(). This
    // matches the backend `compute_metrics()` contract so Savings + Savings
    // Rate + Runway are identical wherever they appear in the app.
    // Transfers are NOT included in any of these — they are pure
    // inter-account movements and would otherwise distort cash flow.
    // Phase 71B — exclude borrowed funds (e.g. payday loan disbursements
    // categorised as "Loan Received") from real income. They show as
    // type=income on the statement but are debt, not earnings.

    const income = scopedTx.filter(isRealIncome).reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
    // Phase 79.x — legacy-data defence: broker BUY / Reinvestment rows that
    // were saved as type='expense' should NOT count as spending. See
    // /app/frontend/src/lib/investmentTransferRules.js. The Dashboard uses
    // one shared filter so every downstream figure (top card, category
    // pie, trend chart) tells the same story.
    const isRealExpense = (t) =>
      t.type === 'expense' && !isInvestmentTransfer(t.description, t.merchant);
    const expenses = scopedTx.filter(isRealExpense).reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
    const investments = scopedTx.filter((t) => t.type === 'investment').reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
    const refunds = scopedTx.filter((t) => t.type === 'refund').reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
    const transfersCount = scopedTx.filter((t) => t.type === 'transfer').length;
    const monthly_expenses = Number(onb?.monthly_expenses || 0) || expenses || 1;
    const baseSavings = Number(onb?.existing_investments || 0);
    // Phase 55 follow-up — prefer the holistic /api/networth/breakdown
    // total (accounts + deposits + holdings) when available. Falls back to
    // the holdings-only calc so the tile never reads "₹0" purely because
    // the breakdown call is still in flight.
    const holdingsAssets = holdings.filter((h) => h.kind === 'asset').reduce((s, h) => s + Number(h.value || 0), 0);
    const holdingsLiab = holdings.filter((h) => h.kind === 'liability').reduce((s, h) => s + Number(h.value || 0), 0);
    const useApi = networthBreakdown && typeof networthBreakdown.total_assets === 'number';
    const assets = useApi ? networthBreakdown.total_assets : holdingsAssets;
    const liabilities = useApi ? networthBreakdown.total_liabilities : holdingsLiab;
    const netWorth = useApi ? networthBreakdown.net_worth : (holdingsAssets - holdingsLiab);
    const savingsForRunway = assets > 0 ? assets : baseSavings;
    const passive = Number(onb?.passive_income || 0);
    // Phase 56 authoritative definitions (used everywhere in 2120):
    //   savings       = income − expenses (investments are NOT subtracted;
    //                   they are an asset conversion, not a cash outflow)
    //   savings_rate  = savings / income, clamped to [-100, +100] for display
    //   surplus       = legacy alias for savings; kept so older consumers don't break
    //   runway_months = liquid buffer / monthly expense baseline
    const savings = income - expenses;
    const surplus = savings;
    const savings_rate_raw = income > 0 ? (savings / income) * 100 : 0;
    const savings_rate = Math.max(-100, Math.min(100, savings_rate_raw));
    const runway = monthly_expenses > 0 ? savingsForRunway / monthly_expenses : 0;
    const passive_coverage = monthly_expenses > 0 ? (passive / monthly_expenses) * 100 : 0;
    const expense_load = income > 0 ? (expenses / income) * 100 : 0;

    // ---- Clarity Score (mirrors backend compute_metrics) -------------------
    // Earned, not inflated. Weights add to 100. Three visibility stages.
    const n_txns = txns.length;
    const monthSet = new Set();
    let income_months_set = new Set();
    txns.forEach((t) => {
      const ym = String(t.txn_date || '').slice(0, 7);
      if (ym) {
        monthSet.add(ym);
        if (isRealIncome(t)) income_months_set.add(ym);
      }
    });
    const months_active = monthSet.size;
    const has_income_txn = txns.some(isRealIncome);
    const has_expense_txn = txns.some((t) => t.type === 'expense');
    const has_imported_statement = txns.some((t) => (t.source === 'pdf' || t.source === 'csv'));
    const created = profile?.created_at ? new Date(profile.created_at) : null;
    const days_since_signup = created ? Math.max(0, Math.floor((now - created) / 86400000)) : 0;

    // Stage gating
    let score_stage = 'none';
    let score_confidence = 'low';
    if ((n_txns < 10 && !has_imported_statement && days_since_signup < 7) || n_txns < 3) {
      score_stage = 'none';
      score_confidence = 'low';
    } else if (n_txns >= 100 || months_active >= 2 || (has_imported_statement && n_txns >= 30)) {
      score_stage = 'established';
      score_confidence = 'high';
    } else {
      score_stage = 'early';
      score_confidence = 'medium';
    }

    // Components
    const c_coverage = (n_txns >= 100 || months_active >= 3) ? 20
      : n_txns >= 30 ? 15 : n_txns >= 10 ? 10 : n_txns >= 3 ? 5 : 0;
    const c_income = income_months_set.size >= 2 ? 15 : has_income_txn ? 8 : 0;
    const expense_txns = txns.filter((t) => t.type === 'expense');
    const categorized = expense_txns.filter((t) => (t.category || 'Miscellaneous') !== 'Miscellaneous').length;
    const c_cat = expense_txns.length ? Math.round((categorized / expense_txns.length) * 10) : 0;
    const c_savings = savings_rate > 20 ? 15 : savings_rate > 10 ? 10 : savings_rate > 0 ? 5 : 0;
    const recActive = (recurring || []).filter((r) => !r.archived);
    const c_recurring = recActive.length >= 3 ? 10 : recActive.length >= 1 ? 5 : 0;
    const activeGoals = (goals || []).filter((g) => (g.status || 'active') === 'active');
    const datedGoals = activeGoals.filter((g) => g.target_date);
    const c_goals = (activeGoals.length >= 2 && datedGoals.length >= 1) ? 10 : activeGoals.length >= 1 ? 5 : 0;
    const c_runway = runway >= 6 ? 10 : runway >= 3 ? 7 : runway >= 1 ? 3 : 0;
    const hasLiab = holdings.some((h) => h.kind === 'liability');
    const hasEmi = !!(onb?.emi_amount) || !!(onb?.has_debt);
    const debtAck = hasLiab || hasEmi || (onb && 'has_debt' in onb && onb.has_debt === false);
    const c_debt = debtAck ? 5 : 0;
    const hasInvTxn = txns.some((t) => t.type === 'investment');
    const hasAssetHolding = holdings.some((h) => h.kind === 'asset');
    const c_invest = (hasInvTxn || hasAssetHolding || baseSavings > 0) ? 5 : 0;

    const stability_raw = c_coverage + c_income + c_cat + c_savings + c_recurring + c_goals + c_runway + c_debt + c_invest;
    const stability = Math.max(0, Math.min(100, stability_raw));

    const score_components = [
      { key: 'transaction_coverage', label: 'Transaction coverage', value: c_coverage, max: 20 },
      { key: 'income_visibility', label: 'Income visibility', value: c_income, max: 15 },
      { key: 'savings_consistency', label: 'Savings consistency', value: c_savings, max: 15 },
      { key: 'expense_categorization', label: 'Expense categorization', value: c_cat, max: 10 },
      { key: 'recurring_awareness', label: 'Recurring awareness', value: c_recurring, max: 10 },
      { key: 'goal_intentionality', label: 'Goal intentionality', value: c_goals, max: 10 },
      { key: 'runway_stability', label: 'Runway stability', value: c_runway, max: 10 },
      { key: 'debt_awareness', label: 'Debt awareness', value: c_debt, max: 5 },
      { key: 'investment_visibility', label: 'Investment visibility', value: c_invest, max: 5 },
    ];

    const catMap = {};
    scopedTx.filter(isRealExpense).forEach((t) => {
      const c = t.category || 'Miscellaneous';
      // Expense amounts are stored as NEGATIVE values in our schema (so a
      // ₹2,400 spend has amount=-2400). Recharts cannot render slices for
      // negative `dataKey` values, it silently drops them, making the
      // donut appear blank. Always sum with Math.abs() so the pie reflects
      // the *magnitude* of spending per category.
      catMap[c] = (catMap[c] || 0) + Math.abs(Number(t.amount));
    });
    // Phase 54 hotfix v2.7.x, when a user has many fine-grained
    // categories (LLM-generated subcategories etc.), the donut becomes a
    // fragmented mess of 20-30 tiny slivers. Roll long-tail categories up
    // into a single "Other" slice so the chart shows a clean top-N + Other.
    const TOP_N = 8;
    const allCats = Object.entries(catMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    let categoryData;
    if (allCats.length <= TOP_N) {
      categoryData = allCats;
    } else {
      const top = allCats.slice(0, TOP_N);
      const otherValue = allCats.slice(TOP_N).reduce((s, c) => s + c.value, 0);
      if (otherValue > 0) top.push({ name: 'Other', value: otherValue, _isRollup: true });
      categoryData = top;
    }

    const trend = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const slice = txns.filter((t) => {
        const td = new Date(t.txn_date);
        return td >= d && td < next;
      });
      // Expense rows are stored with NEGATIVE amounts in our schema (so a
      // ₹2,400 spend has amount=-2400). For the "Monthly spending trend"
      // visualisation users intuitively expect spending to rise UPWARD as
      // it grows, so we flip the sign here, i.e. higher bar = more spent.
      // The Income vs Expenses chart consumes the same field and still
      // works correctly (income remains positive, expenses now also
      // positive → both render above the baseline, side by side).
      //
      // Legacy-data defence (Phase 79.x): shared with isRealExpense above.
      const ex = slice
        .filter(isRealExpense)
        .reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
      const inc = slice.filter(isRealIncome).reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
      trend.push({ label: d.toLocaleDateString(undefined, { month: 'short' }), expenses: Math.round(ex), income: Math.round(inc) });
    }
    const topCategories = [...categoryData].sort((a, b) => b.value - a.value).slice(0, 5);

    // v3.7.6 Phase 84.30 — Net Worth journey. Compute the approximate
    // change in net worth over the last 30 and 180 days by summing
    // signed cash flow (income − expense) across ALL loaded transactions
    // in each window. Investment moves and transfers are ignored —
    // they don't change net worth, they just shift where the money
    // lives. Multi-currency txns collapse to base via `_baseAmount`
    // (set by the currency context before txns land in this array),
    // falling back to the native amount for legacy rows.
    const nowJ = new Date();
    const cutoff30  = new Date(nowJ.getFullYear(), nowJ.getMonth(), nowJ.getDate() - 30);
    const cutoff180 = new Date(nowJ.getFullYear(), nowJ.getMonth(), nowJ.getDate() - 180);
    let nwDelta30 = 0;
    let nwDelta180 = 0;
    txns.forEach((t) => {
      const d = t.txn_date ? new Date(t.txn_date) : null;
      if (!d) return;
      const amt = Number(t._baseAmount != null ? t._baseAmount : t.amount) || 0;
      let signed = 0;
      if (isRealIncome(t))      signed = Math.abs(amt);
      else if (isRealExpense(t)) signed = -Math.abs(amt);
      else return;
      if (d >= cutoff180) nwDelta180 += signed;
      if (d >= cutoff30)  nwDelta30  += signed;
    });

    return {
      income, expenses, investments, savings, surplus, savings_rate, runway, passive_coverage, expense_load,
      refunds, transfersCount,
      stability,
      score_stage, score_confidence, score_components,
      n_txns, months_active, has_imported_statement,
      categoryData, trend, topCategories,
      passive, assets, liabilities, netWorth,
      nwDelta30, nwDelta180,
      activeGoalsCount: activeGoals.length,
      scopedCount: scopedTx.length,
    };
  }, [txns, onb, holdings, goals, recurring, profile, scopeRange, networthBreakdown]);

  const loadInsights = async (force = false) => {
    if (!session?.access_token) return;
    setInsightsLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/insights/generate`, {
        period_days: 60,
        polish_with_ai: !!profile?.want_ai_insights,
        save: true,
        force,
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setInsights(res.data.insights || []);
    } catch (e) { console.warn('insights load failed', e); }
    finally { setInsightsLoading(false); }
  };

  // Phase B — pull the top open questions for the dashboard tile.
  // Cheap read from persisted `user_questions` — no on-the-fly compute.
  const loadQuestions = useCallback(async () => {
    if (!session?.access_token) return;
    setQuestionsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/questions?status=open&limit=3`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        timeout: 10000,
      });
      setQuestions(res.data?.items || []);
    } catch (e) {
      // Never block the dashboard on a questions failure.
      console.warn('questions load failed:', e?.message);
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (!session?.access_token) return;
    loadQuestions();
  }, [session?.access_token, loadQuestions]);

  // Phase 74B — Load the FinancialSnapshot once for the DNA card. Runs
  // in parallel with the rest of the dashboard bootstrap; the card renders
  // a loading skeleton in the meantime.
  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    setDnaLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/snapshot`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          timeout: 15000,
        });
        if (!cancelled) {
          setDna(r.data?.dna || null);
          setSnapshot(r.data || null);
        }
      } catch (e) {
        console.warn('dna load failed:', e?.message);
      } finally {
        if (!cancelled) setDnaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  // Phase 80 — compute the upload reminder from the fetched snapshot.
  // Non-nagging: only fires when (stale >14d) OR (last 5 days of month)
  // OR (first 3 days of new month). Not dismissible; goes away naturally
  // once the user uploads or adds a transaction.
  const uploadReminder = useUploadReminder(snapshot);

  // Phase 9, Cached intelligence:
  // 1) Read cached insights from Supabase first (instant, zero AI cost).
  //    Only call the backend if cache is empty/stale → backend then decides
  //    whether to regenerate or return cache.
  // 2) Trigger today's snapshot in background (lazy-write, one DB row/day).
  // 3) Trigger current-month reflection generation only if missing.
  useEffect(() => {
    if (loading || !user?.id || !session?.access_token) return;
    (async () => {
      const nowIso = new Date().toISOString();
      const { data: cached } = await supabase
        .from('ai_insights')
        .select('*')
        .gt('stale_after', nowIso)
        .order('computed_at', { ascending: false })
        .limit(20);
      if (cached && cached.length > 0) {
        setInsights(cached.map((c) => ({
          title: c.title, body: c.body,
          insight_type: c.insight_type, icon: c.icon, data: c.data || {},
        })));
      } else {
        loadInsights(false);
      }
      // Daily snapshot (lazy-write), fire and forget
      axios.get(`${BACKEND_URL}/api/snapshots/today`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      }).catch(() => {});
      // Monthly reflection, generate only if missing
      const monthIso = new Date().toISOString().slice(0, 7);
      const { data: reflRow } = await supabase
        .from('monthly_reflections')
        .select('id')
        .gte('month', `${monthIso}-01`)
        .lt('month', `${monthIso}-32`)
        .limit(1)
        .maybeSingle();
      if (!reflRow) {
        axios.post(`${BACKEND_URL}/api/reflections/generate-month`, {
          month: monthIso, polish_with_ai: true, save: true, force: false,
        }, {
          headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 60000,
        }).catch(() => {});
      }
    })();
    // eslint-disable-next-line
  }, [loading, user?.id]);

  const hasTxns = txns.length > 0;
  // hasHoldings now means "anything contributes to net worth" — manual
  // holdings OR linked accounts/deposits via the API breakdown.
  const hasHoldings = holdings.length > 0 || (
    !!networthBreakdown && (
      (networthBreakdown.total_assets || 0) > 0 ||
      (networthBreakdown.total_liabilities || 0) > 0
    )
  );
  const hasData = hasTxns || hasHoldings;

  // Phase 55 follow-up #6 — Next-best-step nudge.
  //
  // After the welcome tour finishes, the dashboard for a fresh user is
  // necessarily sparse. Rather than leaving them to guess what to do
  // next, we surface one concrete suggestion based on what's missing.
  // Priority order (most-impactful first):
  //   1. No transactions yet           → import a statement
  //   2. < 2 linked accounts           → add another account
  //   3. No goals defined              → set a first goal
  //   4. No holdings AND no deposits   → log what you own
  //   5. Single month of transactions  → import older statements
  //   6. None of the above             → null (suppress the card)
  //
  // The card is intentionally not shown once everything's reasonably
  // populated — that's how we know the user has graduated past the
  // "what now?" phase.
  const nextBestStep = useMemo(() => {
    const accCount = accountCount ?? 0;
    const goalCount = (goals || []).length;
    const composition = networthBreakdown?.composition || {};
    const hasAnyHolding = holdings.length > 0 || (composition.deposits_assets || 0) > 0;
    const months = new Set(txns.map((t) => (t.txn_date || '').slice(0, 7))).size;
    if (!hasTxns) {
      return {
        key: 'first_import',
        title: 'Import your first statement',
        body: 'Most people get their first meaningful insight within minutes of importing a bank statement.',
        cta: 'Import a statement',
        to: '/import',
        Icon: Upload,
      };
    }
    if (accCount < 2) {
      return {
        key: 'second_account',
        title: 'Add another account',
        body: 'A single account is a snapshot. Adding a second (credit card, savings, salary account) starts to show the shape of your money.',
        cta: 'Add an account',
        to: '/accounts',
        Icon: Wallet,
      };
    }
    if (goalCount === 0) {
      return {
        key: 'first_goal',
        title: 'What are you building?',
        body: 'Emergency cushion, debt freedom, a down payment, retirement, anything. Naming a goal is how clarity becomes useful.',
        cta: 'Set a goal',
        to: '/goals',
        Icon: Target,
      };
    }
    if (!hasAnyHolding) {
      return {
        key: 'first_holding',
        title: 'Add what you own',
        body: 'Mutual funds, stocks, deposits, property, gold. Anything you own counts toward your net worth picture.',
        cta: 'Add a holding',
        to: '/accounts',
        Icon: Scale,
      };
    }
    if (months <= 1) {
      return {
        key: 'more_history',
        title: 'Import a few more months',
        body: 'A single month is a snapshot. Three months show patterns. Twelve tell a story.',
        cta: 'Import more',
        to: '/import',
        Icon: Upload,
      };
    }
    return null;
  }, [hasTxns, accountCount, goals, holdings, networthBreakdown, txns]);

  // First-time empty state on dashboard
  if (!loading && !hasData) {
    return (
      <div className="space-y-6 fade-in">
        <Card className="rounded-2xl border-border/70 bg-hero-soft">
          <CardContent className="p-8 sm:p-10">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /> Welcome</div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Hello {profile?.name?.split(' ')[0] || 'there'}, let's build your picture.</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">2120 grows with you as you add what you have and what flows in and out. No rush, no judgment. Pick a starting point below.</p>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <Link to="/transactions"><Card className="rounded-2xl border-border/70 card-lift cursor-pointer"><CardContent className="p-4"><Plus className="h-5 w-5 text-primary mb-2" /><div className="text-sm font-medium">Add a transaction</div><div className="text-xs text-muted-foreground mt-1">Manually, or scan a receipt.</div></CardContent></Card></Link>
              <Link to="/import"><Card className="rounded-2xl border-border/70 card-lift cursor-pointer"><CardContent className="p-4"><Upload className="h-5 w-5 text-primary mb-2" /><div className="text-sm font-medium">Import a statement</div><div className="text-xs text-muted-foreground mt-1">Drop a CSV. We'll do the rest.</div></CardContent></Card></Link>
              <Link to="/accounts"><Card className="rounded-2xl border-border/70 card-lift cursor-pointer"><CardContent className="p-4"><Scale className="h-5 w-5 text-primary mb-2" /><div className="text-sm font-medium">Map your net worth</div><div className="text-xs text-muted-foreground mt-1">Assets, liabilities, the long view.</div></CardContent></Card></Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <EmptyKpi title="Income & expenses" body="Once you add a few transactions, your monthly flow will appear here." />
          <EmptyKpi title="Net worth" body="Add savings, FDs, MFs, loans and EMI to see your wealth come into focus." />
          <EmptyKpi title="Insights" body="Calm, non-judgmental reflections will appear as your data grows." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase 70I.5 — One-time post-remap banner. Self-mounts; renders
          nothing if there's no active remap for this user. */}
      <CategoryRemapNoticeBanner />

      {/* Phase 85.5 — ask which currency to report in rather than falling back
          to rupees. Renders nothing once the person has told us. */}
      {!profile?.base_currency && (
        <ReportingCurrencyPrompt
          profile={profile}
          token={session?.access_token}
          onSaved={refreshProfile}
        />
      )}

      {/* Phase 80 — Upload reminder tile. Renders only when the reminder
          hook detects staleness / month-end / new-month conditions. */}
      {uploadReminder && <UploadReminderTile reminder={uploadReminder} />}

      {/* Financial Clarity Score hero card, stage-aware */}
      <Card className="rounded-2xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg overflow-hidden">
        <CardContent className="p-6 sm:p-8 relative">
          {metrics.score_stage === 'none' ? (
            <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="clarity-score-building">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 text-xs text-primary-foreground/70">
                  <Sparkles className="h-3.5 w-3.5 text-gold" /> Financial Clarity
                </div>
                <div className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                  Building your clarity&hellip;
                </div>
                <p className="text-sm text-primary-foreground/70 mt-3 max-w-md leading-relaxed">
                  Your score will appear once we&rsquo;ve understood your financial flow. Add a few transactions or import a statement to begin , awareness comes before the number.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Link to="/transactions"><Button size="sm" className="rounded-xl bg-gold text-foreground hover:bg-gold/90" data-testid="building-add-txn">Add a transaction</Button></Link>
                  <Link to="/import"><Button size="sm" variant="ghost" className="rounded-xl text-primary-foreground hover:bg-white/10" data-testid="building-import-statement">Import a statement</Button></Link>
                </div>
              </div>
              <ScoreRing value={null} placeholder />
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 text-xs text-primary-foreground/70">
                  <Sparkles className="h-3.5 w-3.5 text-gold" /> Financial Clarity Score
                  {metrics.score_stage === 'early' && (
                    <Badge variant="outline" className="rounded-full text-[9px] px-2 py-0 border-gold/40 text-gold" data-testid="clarity-early-badge">
                      Early estimate
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <CountUp value={metrics.stability} className={`font-display text-5xl sm:text-6xl font-semibold tabular-nums tracking-tight ${metrics.score_stage === 'early' ? 'opacity-80' : ''}`} />
                  <div className="text-base text-primary-foreground/60">/ 100</div>
                </div>
                <div className="mt-2 text-sm text-gold flex items-center gap-1.5">
                  <ArrowDownRight className="h-3.5 w-3.5 rotate-[135deg]" />
                  {metrics.score_stage === 'early'
                    ? 'Stabilizing as your data grows'
                    : metrics.stability >= 70 ? 'You are tracking well'
                    : metrics.stability >= 40 ? 'Improving steadily'
                    : 'Just getting started'}
                </div>
                <p className="text-sm text-primary-foreground/70 mt-3 max-w-md leading-relaxed">
                  {metrics.score_stage === 'early'
                    ? "An early read. The score will settle as more transactions and a full month of data accumulate."
                    : metrics.stability >= 70 ? "Strong momentum. Consistency compounds."
                    : metrics.stability >= 40 ? "Good progress. Keep building your financial future."
                    : "Each transaction added, each holding mapped, raises this score."}
                </p>
              </div>
              <ScoreRing value={metrics.stability} muted={metrics.score_stage === 'early'} />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-scope-chips">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {SCOPE_PRESETS.map((p) => {
            const isActive = scopePreset === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setScopePreset(p.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${isActive
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                  : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
                data-testid={`dashboard-scope-${p.value}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {hasTxns && metrics.scopedCount > 0 && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {metrics.scopedCount} {metrics.scopedCount === 1 ? 'transaction' : 'transactions'} in {scopeLabel}
          </span>
        )}
      </div>

      {/* Phase 56 — Cash-flow vs Balance-sheet rebuild.
          The tile strip is now: Income, Expenses, Savings, Liabilities,
          Net Worth, Runway. Investments has moved off the strip (it's an
          asset conversion, not a cash outflow) and is surfaced as a quiet
          one-line caption underneath. Transfers are never counted in
          Income/Expenses/Investments.
          Multi-currency users still get the FX-aware overlay: we read
          totals from `/api/dashboard/breakdown` which already provides
          base-converted numbers per metric. */}
      {(() => {
        const multi = fxBreakdown && fxBreakdown.currencies_in_use && fxBreakdown.currencies_in_use.length > 1;
        const income = multi ? fxBreakdown.total_income : metrics.income;
        const expenses = multi ? fxBreakdown.total_expenses : metrics.expenses;
        const investments = multi ? (fxBreakdown.total_investments ?? metrics.investments) : metrics.investments;
        // Savings derived from the SAME source as income/expenses so the
        // tile is internally consistent even when the user toggles between
        // single-currency and FX-aware views.
        const savings = (multi && typeof fxBreakdown.total_surplus === 'number')
          ? fxBreakdown.total_surplus
          : metrics.savings;
        const liabilities = metrics.liabilities || 0;
        const liabilitiesEmpty = !hasHoldings && !(metrics.assets > 0);
        const savingsClass = savings < 0 ? 'text-[hsl(var(--destructive))]' : '';
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
              <KPI
                label="Income"
                value={formatMoney(income, currency, { compact: true })}
                icon={ArrowDownRight}
                testid="kpi-income"
                empty={!hasTxns}
                emptyHint="No income yet"
              />
              <KPI
                label="Expenses"
                value={formatMoney(expenses, currency, { compact: true })}
                icon={ArrowUpRight}
                testid="kpi-expenses"
                empty={!hasTxns}
                emptyHint="Add to begin"
                hint="Real spend in this period. Excludes inter-account transfers and money you moved into investments."
              />
              <KPI
                label="Net cash flow"
                value={formatMoney(savings, currency, { compact: true })}
                icon={PiggyBank}
                testid="kpi-savings"
                empty={!hasTxns}
                valueClass={savingsClass}
                subValue={savings < 0
                  ? (<><span aria-hidden="true">▼ </span>Used {formatMoney(Math.abs(savings), currency, { compact: true })} from savings</>)
                  : null}
                subValueClass={savings < 0 ? 'text-[hsl(var(--destructive))]' : undefined}
                hint="Income minus Expenses for this period. When negative, it means you dipped into savings or other accounts to cover the gap. Investments aren't subtracted here — they're an asset conversion, not a cash outflow."
              />
              <KPI
                label="Liabilities"
                value={liabilities > 0
                  ? `-${formatMoney(liabilities, currency, { compact: true })}`
                  : formatMoney(0, currency, { compact: true })}
                icon={CreditCard}
                testid="kpi-liabilities"
                empty={liabilitiesEmpty}
                emptyHint="Add loans / cards"
                linkTo="/accounts"
                valueClass={liabilities > 0 ? 'text-[hsl(var(--destructive))]' : ''}
                hint="Outstanding loans, credit-card balances, and any liabilities you've added on the Accounts page."
              />
              <KPI
                label="Net worth"
                value={formatMoney(metrics.netWorth, currency, { compact: true })}
                icon={Scale}
                testid="kpi-net-worth"
                empty={!hasHoldings && !(metrics.assets > 0)}
                emptyHint="Add holdings"
                linkTo="/accounts"
                hint="Assets minus liabilities, holistic across linked accounts, deposits, and manual holdings. Set a current balance on each account to make this accurate."
                {...buildNetWorthJourney({
                  netWorth: metrics.netWorth,
                  delta30: metrics.nwDelta30,
                  delta180: metrics.nwDelta180,
                  currency,
                })}
              />
              <KPI
                label="Runway"
                value={`${metrics.runway.toFixed(1)} mo`}
                icon={ShieldCheck}
                testid="kpi-runway"
                empty={metrics.runway === 0}
                hint="How long your liquid buffer would cover essentials if income paused today."
              />
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground leading-relaxed" data-testid="kpi-disclosure">
              {investments > 0 ? (
                <>
                  <span className="font-medium text-foreground tabular-nums">{formatMoney(investments, currency, { compact: true })}</span>
                  {' moved to investments in this period (tracked separately as wealth, not spend).'}
                </>
              ) : (
                'Transfers between your own accounts, money you moved to investments, and loan/borrowed funds are excluded from Income and Expenses.'
              )}
            </div>
          </>
        );
      })()}

      {/* v3.7.5 Phase 84.29 — Debt Focus preview. Shows the top-3 highest
          interest liabilities so users see instantly which loan is the
          fastest win. Renders itself null when there are no ranked debts. */}
      {liabilities.length > 0 && (
        <DebtFocusTable items={liabilities} currency={currency} maxRows={3} compact />
      )}

      {/* Phase 56 — Manual balance nudge. Net Worth + Runway can only be
          trustworthy when the user has set a current balance on each of
          their linked bank/cash/wallet accounts (statements don't carry
          opening balances, so we can't infer them). We only show this if
          at least one account is missing a balance, and only when the
          user has at least one account on file. */}
      {/* Phase 74 — Life Profile completeness nag. Server-gated: only
          shows for users under 90% completeness, and snoozes for 7 days
          on dismiss. Silently hides if the migration hasn't been applied. */}
      <LifeProfileBanner />

      {accountsMissingBalance > 0 && (
        <Card
          className="rounded-2xl border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/5"
          data-testid="balances-nudge"
        >
          <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="h-9 w-9 rounded-xl bg-card border border-border/70 flex items-center justify-center shrink-0">
              <Wallet className="h-4 w-4 text-[hsl(var(--gold))]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium" data-testid="balances-nudge-title">
                {accountsMissingBalance === 1
                  ? '1 account is missing a current balance'
                  : `${accountsMissingBalance} accounts are missing a current balance`}
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Statements don't include opening balances, so Net Worth and Runway can only be accurate once you set today's balance for each account. Takes about 30 seconds per account.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/accounts')}
              className="rounded-xl w-full sm:w-auto shrink-0"
              data-testid="balances-nudge-cta"
            >
              Set balances <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase 55 follow-up #6 — Next best step nudge. One concrete
          suggestion based on what's most-impactfully missing. Suppressed
          once the user has imported, has 2+ accounts, has at least one
          goal and one holding, and 2+ months of history. */}
      {nextBestStep && (
        <Card
          className="rounded-2xl border-primary/30 bg-primary/5"
          data-testid={`next-best-step-${nextBestStep.key}`}
        >
          <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-card border border-border/70 flex items-center justify-center shrink-0">
              <nextBestStep.Icon className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Next best step</div>
              <div className="font-display text-lg font-semibold mt-0.5" data-testid="next-best-step-title">{nextBestStep.title}</div>
              <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{nextBestStep.body}</div>
            </div>
            <Button
              onClick={() => navigate(nextBestStep.to)}
              className="rounded-xl w-full sm:w-auto shrink-0"
              data-testid="next-best-step-cta"
            >
              {nextBestStep.cta} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase 38.4, disclosure + breakdown when multi-currency. Hidden
          entirely for single-currency users so there's no noise. */}
      {fxBreakdown && fxBreakdown.currencies_in_use && fxBreakdown.currencies_in_use.length > 1 && (
        <FxBreakdownCard
          testId="dashboard-fx-breakdown"
          base={fxBreakdown.base_currency}
          breakdown={fxBreakdown.breakdown}
          headline={fxBreakdown}
          warnings={fxBreakdown.warnings}
          valueColumns={[
            { key: 'income',   label: 'Income',   nativeKey: 'native_income',   baseKey: 'base_income' },
            { key: 'expenses', label: 'Expenses', nativeKey: 'native_expenses', baseKey: 'base_expenses' },
            { key: 'surplus',  label: 'Net cash flow',  nativeKey: 'native_surplus',  baseKey: 'base_surplus' },
          ]}
        />
      )}

      {/* Refunds received, shown only when present, kept visually quiet so it
          doesn't compete with the earned-income KPI. Refunds are intentionally
          NOT counted as income so the Clarity Score stays honest. */}
      {metrics.refunds > 0 && (
        <div
          className="flex items-center gap-2.5 rounded-xl border border-dashed border-border/60 bg-muted/20 px-3.5 py-2 text-xs text-muted-foreground"
          data-testid="dashboard-refunds-note"
        >
          <RotateCcw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span>
            You also received{' '}
            <span className="font-medium text-foreground tabular-nums">
              {formatMoney(metrics.refunds, currency, { compact: true })}
            </span>{' '}
            in refunds &amp; reversals {scopeLabel === 'all' ? '' : `in ${scopeLabel}`}.
            <span className="opacity-70"> Kept separate from earned income.</span>
          </span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border/70 lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Spending by category</div>
              <PieChartIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            {loading ? <ChartSkeleton /> : metrics.categoryData.length === 0 ? <EmptyChart label="Add a few transactions and your category mix will show up here." /> : (
              <div className="h-56" data-testid="dashboard-chart-category-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={metrics.categoryData}
                      dataKey="value"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                      stroke="hsl(var(--background))"
                      strokeWidth={1}
                      onClick={(payload) => {
                        // Phase 57 — clickable slice. Skip the rollup
                        // "Other" slice (it doesn't map to one category).
                        if (payload && payload.name && !payload._isRollup) {
                          navigate(`/transactions/category/${slugify(payload.name)}`);
                        }
                      }}
                      cursor="pointer"
                    >
                      {metrics.categoryData.map((c) => (
                        <Cell
                          key={c.name}
                          fill={c._isRollup ? '#94a3b8' : colorForCategory(c.name)}
                        />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v) => formatMoney(v, currency)} contentStyle={tooltipStyle()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {metrics.categoryData.slice(0, 6).map((c) => {
                const inner = (
                  <Badge variant="secondary" className="rounded-full text-[10px]">
                    <span className="h-2 w-2 rounded-full mr-1.5" style={{ background: c._isRollup ? '#94a3b8' : colorForCategory(c.name) }} />
                    {c.name}
                  </Badge>
                );
                if (c._isRollup) {
                  return <span key={c.name}>{inner}</span>;
                }
                return (
                  <button
                    type="button"
                    key={c.name}
                    onClick={() => navigate(`/transactions/category/${slugify(c.name)}`)}
                    className="hover:opacity-80 transition-opacity"
                    data-testid={`dash-cat-chip-${c.name}`}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/70 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Monthly spending trend</div>
              <span className="text-xs text-muted-foreground">last 6 months</span>
            </div>
            {loading ? <ChartSkeleton /> : !hasTxns ? <EmptyChart label="Import your first statement to begin building your financial picture." /> : (
              <div className="h-56" data-testid="dashboard-chart-monthly-trend">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metrics.trend} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="clarityExpense" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" opacity={0.35} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                      tickFormatter={(v) => formatMoney(v, currency, { compact: true })}
                    />
                    <RTooltip formatter={(v) => formatMoney(v, currency)} contentStyle={tooltipStyle()} />
                    <Area type="monotone" dataKey="expenses" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#clarityExpense)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/70 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Income vs expenses</div>
              <span className="text-xs text-muted-foreground">last 6 months</span>
            </div>
            {loading ? <ChartSkeleton /> : !hasTxns ? <EmptyChart label="As income and expenses flow in, this view will fill in softly." /> : (
              <div className="h-56" data-testid="dashboard-chart-income-expense">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metrics.trend} barGap={6}>
                    <CartesianGrid stroke="hsl(var(--border))" opacity={0.35} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                      tickFormatter={(v) => formatMoney(v, currency, { compact: true })}
                    />
                    <RTooltip formatter={(v) => formatMoney(v, currency)} contentStyle={tooltipStyle()} />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }} />
                    <Bar dataKey="income" name="Income" fill="hsl(var(--chart-5))" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border/70 lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Top categories</div>
              <span className="text-xs text-muted-foreground">{scopeLabel}</span>
            </div>
            {loading ? <ChartSkeleton /> : metrics.topCategories.length === 0 ? <EmptyChart label="No categories yet, patterns will emerge soon." /> : (
              <div className="space-y-3" data-testid="dashboard-chart-top-categories">
                {metrics.topCategories.map((c) => {
                  const pct = metrics.expenses > 0 ? (c.value / metrics.expenses) * 100 : 0;
                  return (
                    <button
                      type="button"
                      key={c.name}
                      onClick={() => navigate(`/transactions/category/${slugify(c.name)}`)}
                      className="block w-full text-left group hover:opacity-90 transition-opacity"
                      data-testid={`dash-topcat-${c.name}`}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground group-hover:underline underline-offset-2 decoration-dotted">{c.name}</span>
                        <span className="text-muted-foreground tabular-nums">{formatMoney(c.value, currency, { compact: true })} · {pct.toFixed(0)}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: colorForCategory(c.name) }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Phase 57 — left column holds the Financial Health Snapshot and
            the new Spending by Account widget. They stack vertically so
            the left column grows to match the Insights column height,
            eliminating the white-space gap that used to sit under the
            snapshot card. */}
        <div className="lg:col-span-2 space-y-4 flex flex-col">
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /><div className="text-sm font-medium">Financial health snapshot</div></div>
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
                    data-testid="health-snapshot-help-trigger"
                  >
                    What is this?
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md rounded-2xl" data-testid="health-snapshot-help-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-display">Financial health snapshot</DialogTitle>
                    <DialogDescription>
                      A quick read on where your money sits this period. Not a grade, not a verdict, just four numbers that, together, help you see the shape of your finances at a glance.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 text-sm leading-relaxed pt-1">
                    <div>
                      <div className="font-medium text-foreground">Savings rate</div>
                      <div className="text-muted-foreground">What share of your income you kept after expenses and investments. Higher is better.</div>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Expense load</div>
                      <div className="text-muted-foreground">How much of your income went to expenses. Lower means more breathing room.</div>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Passive coverage</div>
                      <div className="text-muted-foreground">The share of your monthly expenses already covered by passive income like rent, dividends, or interest. 100% means your essentials run on their own.</div>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Stability score</div>
                      <div className="text-muted-foreground">A 0–100 read of how steady your finances look right now. It rises as your data grows and your habits hold.</div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              <Stat label="Savings rate" value={formatPercent(metrics.savings_rate, 0)} testid="health-savings-rate" hint="Net Cash Flow / Income for this period (clamped to ±100%). A negative value means you spent more than you earned and drew from savings." />
              <Stat label="Expense load" value={formatPercent(metrics.expense_load, 0)} testid="health-expense-load" hint="How much of your income went to expenses. Lower means more breathing room." />
              <Stat label="Passive coverage" value={formatPercent(metrics.passive_coverage, 0)} testid="health-passive-coverage" hint="The share of your monthly expenses already covered by passive income like rent, dividends, or interest." />
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="space-y-1.5 cursor-help" data-testid="health-stability-score">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Stability score</div>
                      <div className="font-display text-xl font-semibold tabular-nums">{metrics.stability}<span className="text-sm text-muted-foreground">/100</span></div>
                      <Progress value={metrics.stability} className="h-1.5" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs leading-relaxed">
                    A 0&ndash;100 read of how steady your finances look right now. It rises as your data grows and your habits hold.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {metrics.runway >= 3 ? 'If income paused today, you could cover essentials for a while.' : 'Your buffer is building. Each small step adds up.'}
            </div>
          </CardContent>
        </Card>

        {/* Phase 57 — Spending by Account widget. Compact per-account
            rows; clicking one drills into the Account detail page. Fills
            the space under the Snapshot card that used to be blank. */}
        <Card className="rounded-2xl border-border/70 flex-1" data-testid="dashboard-spending-by-account">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-sm font-medium">Spending by account</div></div>
              <span className="text-xs text-muted-foreground">{scopeLabel}</span>
            </div>
            {loading ? (
              <div className="space-y-2"><Skeleton className="h-10 rounded-xl" /><Skeleton className="h-10 rounded-xl" /></div>
            ) : (() => {
              // Phase 57 — Convert the scope's YYYY-MM-DD `from` string to
              // an approximate period-days count so `computeSpendingByAccount`
              // can filter consistently with the other dashboard widgets.
              // For "All time" (no `from`) we pass null which preserves the
              // full transaction history.
              let periodDays = null;
              if (scopeRange.from) {
                const fromDate = new Date(scopeRange.from + 'T00:00:00');
                periodDays = Math.max(1, Math.ceil((Date.now() - fromDate.getTime()) / (1000 * 60 * 60 * 24)));
              }
              const rows = computeSpendingByAccount(txns, allAccountsFull, periodDays);
              if (rows.length === 0) {
                return (
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    Once you have expenses recorded against your accounts, this card will surface where your money is going by account.
                  </div>
                );
              }
              const max = Math.max(1, ...rows.map((r) => r.total));
              return (
                <div className="space-y-3" data-testid="sba-rows">
                  {rows.slice(0, 5).map((r) => {
                    const pct = (r.total / max) * 100;
                    const inner = (
                      <>
                        <div className="flex items-center justify-between text-xs gap-3">
                          <span className="flex items-center gap-2 min-w-0">
                            <Wallet className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate">{r.account_name}</span>
                            {r.topCategory && (
                              <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                                · top: {r.topCategory.name}
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground tabular-nums shrink-0">
                            {formatMoney(r.total, r.account_currency || currency, { compact: true })}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    );
                    if (r.account_id === 'unlinked') {
                      return <div key={r.account_id} data-testid={`sba-row-${r.account_id}`}>{inner}</div>;
                    }
                    return (
                      <button
                        type="button"
                        key={r.account_id}
                        onClick={() => navigate(`/accounts/${r.account_id}/detail`)}
                        className="block w-full text-left group hover:opacity-90 transition-opacity"
                        data-testid={`sba-row-${r.account_id}`}
                      >
                        {inner}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
        </div>

        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5" data-testid="questions-tile">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageCircleQuestion className="h-4 w-4 text-primary" />
                <div className="text-sm font-medium">Questions worth sitting with</div>
              </div>
              <Link
                to="/reflections?tab=prompts"
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="questions-tile-see-all"
              >
                See all
              </Link>
            </div>
            <div className="space-y-3">
              {questionsLoading ? (
                <>
                  <Skeleton className="h-20 w-full rounded-xl" />
                  <Skeleton className="h-20 w-full rounded-xl" />
                </>
              ) : questions.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="questions-tile-empty">
                  You&rsquo;re caught up. New prompts will surface as your data evolves.
                </div>
              ) : (
                questions.map((q) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    token={session?.access_token}
                    onChanged={loadQuestions}
                    compact
                  />
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <DnaCompactCard dna={dna} loading={dnaLoading} />

        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5" data-testid="ai-insights-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /><div className="text-sm font-medium">Financial insights</div></div>
              <Button size="sm" variant="ghost" className="text-xs rounded-xl" onClick={() => loadInsights(false)} disabled={insightsLoading} data-testid="insights-refresh">{insightsLoading ? 'Thinking…' : 'Check again'}</Button>
            </div>
            <div className="space-y-3">
              {insightsLoading ? (<><Skeleton className="h-16 w-full rounded-xl" /><Skeleton className="h-16 w-full rounded-xl" /></>) :
                insights.length === 0 ? (<div className="text-sm text-muted-foreground">As you add transactions, insights will appear here.</div>) :
                insights.slice(0, 4).map((ins, i) => (
                  <div key={i} className="rounded-xl bg-accent/40 border border-border/60 p-3 card-lift">
                    <div className="flex items-center gap-2"><Compass className="h-3.5 w-3.5 text-primary" /><div className="text-sm font-medium">{ins.title}</div></div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{ins.body}</div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase 60A — Fixed Monthly Commitments rollup. Mirrors the totals
          shown on the Recurring page so the dashboard answers "how much of
          my money is already spoken for each month?" at a glance.
          Phase 69B — also surfaces recurring INCOME so the user can see the
          full recurring cash flow shape (in - out = net committed).
          Hidden if the user has no active recurring rows at all. */}
      {(() => {
        const FREQ_DAYS = { monthly: 30, weekly: 7, quarterly: 92, yearly: 365 };
        const recActive = (recurring || []).filter((r) => r.active && !r.archived);
        if (recActive.length === 0) return null;
        // Phase 70A — `transfer` is internal-only and stays OUT of the
        // committed tally (an outflow that's also an inflow elsewhere).
        // We still compute its monthly total so the optional Transfer
        // tile can render, but it never affects net committed cash.
        const ALLOWED_KINDS = ['income', 'expense', 'investment', 'debt_payment', 'transfer'];
        const totals = { income: 0, expense: 0, investment: 0, debt_payment: 0, transfer: 0 };
        recActive.forEach((r) => {
          const days = FREQ_DAYS[r.frequency] || 30;
          const monthly = Number(r.amount || 0) * (30 / days);
          const k = ALLOWED_KINDS.includes(r.kind) ? r.kind : 'expense';
          totals[k] += monthly;
        });
        const commitments = totals.expense + totals.investment + totals.debt_payment;
        const hasIncome = totals.income > 0;
        const hasTransfer = totals.transfer > 0;
        const net = totals.income - commitments;
        const curr = profile?.currency || 'INR';
        const Tile = ({ label, value, accent, tone }) => (
          <div className="flex-1 min-w-[140px]" data-testid={`dash-commitments-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div
              className={`mt-0.5 font-display tabular-nums ${accent ? 'text-xl font-semibold' : 'text-lg font-semibold'} ${
                tone === 'positive' ? 'text-emerald-600 dark:text-emerald-400'
                : tone === 'negative' ? 'text-red-600 dark:text-red-400'
                : tone === 'neutral' ? 'text-muted-foreground'
                : accent ? 'text-primary' : ''
              }`}
            >
              {tone === 'positive' && value > 0 ? '+' : ''}
              {formatMoney(value, curr, { compact: true })}
              <span className="text-[10px] text-muted-foreground font-normal">/mo</span>
            </div>
          </div>
        );
        return (
          <Card className="rounded-2xl border-border/70" data-testid="dash-fixed-commitments">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-primary" />
                  <div className="text-sm font-medium">{hasIncome ? 'Recurring Cash Flow' : 'Fixed Monthly Commitments'}</div>
                </div>
                <Link to="/recurring" className="text-xs text-primary hover:underline">Manage recurring →</Link>
              </div>
              <div className="flex flex-wrap items-end gap-5">
                {hasIncome && <Tile label="Income" value={totals.income} tone="positive" />}
                <Tile label="Expenses" value={totals.expense} />
                <Tile label="Investments" value={totals.investment} />
                <Tile label="Debt payments" value={totals.debt_payment} />
                {/* Phase 70A — internal transfers shown in a muted tone with
                    a small "(internal)" label so users see them without
                    them feeling like a "real" commitment. */}
                {hasTransfer && <Tile label="Transfers" value={totals.transfer} tone="neutral" />}
                <div className="hidden sm:block h-10 w-px bg-border/70" />
                {hasIncome
                  ? <Tile label="Net committed" value={net} accent tone={net >= 0 ? 'positive' : 'negative'} />
                  : <Tile label="Total committed" value={commitments} accent />}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      <Card className="rounded-2xl border-border/70">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <div className="text-sm font-medium">Steadily building toward</div>
            </div>
            <Link to="/goals" className="text-xs text-primary hover:underline" data-testid="dashboard-goals-link">
              {goals.length > 0 ? 'View all goals' : 'Set a direction'}
            </Link>
          </div>
          {loading ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : goals.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 p-5 text-sm text-muted-foreground leading-relaxed" data-testid="dashboard-goals-empty">
              No active goals yet. Goals in 2120 aren&rsquo;t targets to hit. They&rsquo;re directions to walk in.&nbsp;
              <Link to="/goals" className="text-primary hover:underline">Pick one to begin.</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="dashboard-goals-preview">
              {goals.slice(0, 2).map((g) => {
                const pct = Math.min(100, Math.round((Number(g.current_amount || 0) / Math.max(1, Number(g.target_amount))) * 100));
                return (
                  <Link key={g.id} to="/goals" className="block">
                    <div className="rounded-xl border border-border/70 p-4 card-lift" data-testid="dashboard-goal-row">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{g.title}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {g.target_date
                              ? `By ${new Date(g.target_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
                              : 'A direction without a date'}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums shrink-0">{pct}%</div>
                      </div>
                      <Progress value={pct} className="h-1.5 mt-3" />
                      <div className="text-[11px] text-muted-foreground mt-2 tabular-nums">
                        {formatMoney(g.current_amount || 0, g.currency || currency, { compact: true })} / {formatMoney(g.target_amount, g.currency || currency, { compact: true })}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          {metrics.intentionalityBoost > 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-[hsl(var(--gold))]" />
              Goals add a steady +{metrics.intentionalityBoost} to your Clarity Score , intention counts.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border/70">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">Recent activity</div>
            <Link to="/transactions" className="text-xs text-primary hover:underline">View all</Link>
          </div>
          {loading ? (<><Skeleton className="h-12 w-full rounded-xl mb-2" /><Skeleton className="h-12 w-full rounded-xl mb-2" /><Skeleton className="h-12 w-full rounded-xl" /></>) :
            txns.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                No transactions yet. <Link to="/transactions" className="text-primary">Add your first one</Link> to start seeing patterns.
              </div>
            ) : (
              <div className="space-y-2">
                {txns.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-2.5" data-testid="recent-transaction-row">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.description || t.category || 'Transaction'}</div>
                      <div className="text-xs text-muted-foreground">{t.txn_date} · {t.category || ' '}</div>
                    </div>
                    <div className={`text-sm font-medium tabular-nums ${t.type === 'income' ? 'text-[hsl(var(--chart-5))]' : t.type === 'transfer' ? 'text-muted-foreground' : t.type === 'refund' ? 'text-muted-foreground' : ''}`}>
                      {t.type === 'income' ? '+' : t.type === 'investment' ? '↑' : t.type === 'transfer' ? '⇄ ' : t.type === 'refund' ? '↺ ' : '-'}{formatMoney(Math.abs(Number(t.amount)), currency, { compact: true })}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, value, icon: Icon, testid, empty, emptyHint, linkTo, hint, valueClass, subValue, subValueClass }) {
  // Phase 55 follow-up #8 — optional `hint` renders the label as a
  // hoverable tooltip. Used by the Investments tile to disclose that
  // it only counts in-period investment transactions, not holdings.
  // Phase 56 — `valueClass` lets callers tint the figure (e.g. red for
  // a negative Net Cash Flow or a Liabilities tile rendered with a
  // leading minus).
  // Phase 56.1 — `subValue` renders a small subtitle under the main
  // figure. Used by the Net Cash Flow tile to surface
  // "▼ Used ₹X from savings" when cash flow is negative, so the user
  // understands what the negative number means rather than reading
  // it as broken.
  const labelNode = hint ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted underline-offset-2 decoration-muted-foreground/40">{label}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <span>{label}</span>
  );
  const card = (
    <Card className="rounded-2xl border-border/70 card-lift h-full" data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {labelNode}
          <Icon className="h-4 w-4" />
        </div>
        {empty ? (
          <div className="mt-2 text-xs text-muted-foreground leading-snug">{emptyHint || 'No data yet'}</div>
        ) : (
          <>
            <div className={`mt-2 font-display text-xl sm:text-2xl font-semibold tabular-nums ${valueClass || ''}`}>{value}</div>
            {subValue ? (
              <div className={`mt-1 text-[11px] leading-snug ${subValueClass || 'text-muted-foreground'}`} data-testid={testid ? `${testid}-sub` : undefined}>{subValue}</div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
  return linkTo ? <Link to={linkTo}>{card}</Link> : card;
}

function EmptyKpi({ title, body }) {
  return (
    <Card className="rounded-2xl border-dashed border-border/70">
      <CardContent className="p-4">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, testid, hint }) {
  const body = (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
  if (!hint) return body;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{body}</div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ChartSkeleton() { return <Skeleton className="h-56 w-full rounded-xl" />; }
function EmptyChart({ label }) {
  return <div className="h-56 rounded-xl border border-dashed border-border/70 grid place-items-center text-xs text-muted-foreground p-4 text-center">{label}</div>;
}
function tooltipStyle() {
  return { borderRadius: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', fontSize: 12 };
}

function ScoreRing({ value, placeholder = false, muted = false }) {
  const size = 140;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const target = Math.max(0, Math.min(100, Number(value) || 0));
  const [animated, setAnimated] = useState(0);
  const rafRef = useRef(null);
  const startedRef = useRef(false);

  // Count-up animation: ease-out over ~1100ms from 0 -> target on first paint
  useEffect(() => {
    if (placeholder) return undefined;
    if (startedRef.current) {
      // subsequent value changes - smooth transition
      const from = animated;
      const to = target;
      const dur = 700;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setAnimated(from + (to - from) * eased);
        if (p < 1) rafRef.current = requestAnimationFrame(tick);
      };
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    }
    startedRef.current = true;
    const dur = 1100;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimated(target * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, placeholder]); // eslint-disable-line

  // Placeholder ring (no number), soft pulsing arc to indicate "building"
  if (placeholder) {
    return (
      <div className="relative" style={{ width: size, height: size }} data-testid="clarity-score-ring-placeholder">
        <svg width={size} height={size} className="block" style={{ overflow: 'visible' }}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={stroke} fill="none" />
          {/* Outer <g> applies the static -90deg orientation; inner spinning group
              gets the CSS keyframe. Splitting these prevents Chrome from
              interpolating two conflicting transforms on the same circle,
              which was producing a deformed/folded arc. */}
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <g
              style={{
                animation: 'clarity-ring-spin 6s linear infinite',
                transformOrigin: `${size / 2}px ${size / 2}px`,
                transformBox: 'view-box',
              }}
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                stroke="rgba(212,169,93,0.55)"
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={`${c * 0.18} ${c}`}
                strokeLinecap="round"
              />
            </g>
          </g>
        </svg>
        <div className="absolute inset-0 grid place-items-center text-center">
          <div className="text-[10px] text-primary-foreground/70 tracking-wider uppercase">Learning</div>
        </div>
      </div>
    );
  }

  const offset = c - (animated / 100) * c;
  const strokeColor = muted ? 'rgba(212,169,93,0.55)' : '#D4A95D';
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="clarity-score-ring">
      <svg width={size} height={size} className="block">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={strokeColor}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className={`absolute inset-0 grid place-items-center text-center ${muted ? 'opacity-80' : ''}`}>
        <div>
          <div className="text-xs text-primary-foreground/70">Clarity</div>
          <div className="font-display text-2xl font-semibold tabular-nums">{Math.round(animated)}</div>
        </div>
      </div>
    </div>
  );
}

function CountUp({ value, duration = 1100, className = '' }) {
  const target = Math.max(0, Math.min(100, Number(value) || 0));
  const [n, setN] = useState(0);
  const rafRef = useRef(null);
  const startedRef = useRef(false);
  const prevRef = useRef(0);

  useEffect(() => {
    const from = startedRef.current ? prevRef.current : 0;
    const to = target;
    startedRef.current = true;
    const dur = startedRef.current && prevRef.current !== 0 ? 700 : duration;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = from + (to - from) * eased;
      setN(next);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else prevRef.current = to;
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return <span className={className}>{Math.round(n)}</span>;
}
